import { useCallback, useEffect, useMemo, useState } from "react";
import { isAndroid, isIOS, isMobile } from "react-device-detect";

const STORAGE_KEY_DISMISSED = "pwa-install-banner-dismissed";
const STORAGE_KEY_INSTALLED = "pwa-installed";

type BeforeInstallPromptEvent = Event & {
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandaloneMode(): boolean {
    return (
        window.matchMedia("(display-mode: standalone)").matches ||
        (window.navigator as Navigator & { standalone?: boolean }).standalone === true
    );
}

function isDismissed(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY_DISMISSED) === "true";
    } catch {
        return false;
    }
}

function isInstalledInStorage(): boolean {
    try {
        return localStorage.getItem(STORAGE_KEY_INSTALLED) === "true";
    } catch {
        return false;
    }
}

function markInstalledInStorage(): void {
    try {
        localStorage.setItem(STORAGE_KEY_INSTALLED, "true");
    } catch {
        // Ignore storage errors (private browsing, etc.)
    }
}

async function isPwaInstalledOnDevice(): Promise<boolean> {
    if (!("getInstalledRelatedApps" in navigator)) {
        return false;
    }

    try {
        const relatedApps = await (
            navigator as Navigator & {
                getInstalledRelatedApps: () => Promise<Array<{ platform: string; url?: string }>>;
            }
        ).getInstalledRelatedApps();

        return relatedApps.some((app) => app.platform === "webapp");
    } catch {
        return false;
    }
}

export type PwaInstallMode = "install" | "ios" | "android";

export function usePwaInstall() {
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [isInstalling, setIsInstalling] = useState(false);
    const [isDismissedState, setIsDismissedState] = useState(isDismissed);
    const [isStandalone, setIsStandalone] = useState(isStandaloneMode);
    const [isInstalled, setIsInstalled] = useState(
        () => isStandaloneMode() || isInstalledInStorage()
    );
    const [isInstallCheckComplete, setIsInstallCheckComplete] = useState(
        () => isStandaloneMode() || isInstalledInStorage()
    );

    useEffect(() => {
        if (!isMobile || isStandalone || isInstalled) {
            setIsInstallCheckComplete(true);
            return;
        }

        let cancelled = false;

        void isPwaInstalledOnDevice().then((installed) => {
            if (cancelled) {
                return;
            }

            if (installed) {
                markInstalledInStorage();
                setIsInstalled(true);
            }

            setIsInstallCheckComplete(true);
        });

        return () => {
            cancelled = true;
        };
    }, [isInstalled, isStandalone]);

    useEffect(() => {
        if (isStandalone || isInstalled || isDismissedState || !isMobile) {
            return;
        }

        const onBeforeInstallPrompt = (event: Event) => {
            event.preventDefault();
            setDeferredPrompt(event as BeforeInstallPromptEvent);
        };

        const onAppInstalled = () => {
            markInstalledInStorage();
            setDeferredPrompt(null);
            setIsInstalled(true);
            setIsStandalone(true);
        };

        const onDisplayModeChange = () => {
            setIsStandalone(isStandaloneMode());
        };

        const mql = window.matchMedia("(display-mode: standalone)");

        window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
        window.addEventListener("appinstalled", onAppInstalled);
        mql.addEventListener("change", onDisplayModeChange);

        return () => {
            window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
            window.removeEventListener("appinstalled", onAppInstalled);
            mql.removeEventListener("change", onDisplayModeChange);
        };
    }, [isDismissedState, isInstalled, isStandalone]);

    const mode: PwaInstallMode | null = useMemo(() => {
        if (isStandalone || isInstalled || isDismissedState || !isMobile || !isInstallCheckComplete) {
            return null;
        }
        if (isIOS && isMobile) {
            return "ios";
        }
        if (deferredPrompt) {
            return "install";
        }
        if (isAndroid && isMobile && "serviceWorker" in navigator) {
            return "android";
        }
        return null;
    }, [deferredPrompt, isDismissedState, isInstallCheckComplete, isInstalled, isStandalone]);

    const dismiss = useCallback(() => {
        try {
            localStorage.setItem(STORAGE_KEY_DISMISSED, "true");
        } catch {
            // Ignore storage errors (private browsing, etc.)
        }
        setIsDismissedState(true);
        setDeferredPrompt(null);
    }, []);

    const install = useCallback(async () => {
        if (!deferredPrompt) {
            return;
        }

        setIsInstalling(true);

        try {
            await deferredPrompt.prompt();
            const choice = await deferredPrompt.userChoice;

            if (choice.outcome === "accepted") {
                markInstalledInStorage();
                setIsInstalled(true);
                setDeferredPrompt(null);
            }
        } finally {
            setIsInstalling(false);
        }
    }, [deferredPrompt]);

    return {
        isVisible: mode !== null,
        mode,
        install,
        dismiss,
        isInstalling,
    };
}
